import { HERO_ALT } from '@/components/landing/inference-layers';

const BASE = '/img/inference-layer/hero';
const WIDTHS = [640, 960, 1280, 1600, 1920, 2400] as const;
const srcset = (ext: string) => WIDTHS.map((w) => `${BASE}-${w}.${ext} ${w}w`).join(', ');

/**
 * Rendered width of the picture, for the browser's pick from the srcset. From
 * lg the figure is min(1040px, 76vw), centred under the headline; below lg it
 * runs edge to edge — except on a landscape phone, where `max-height: 75vh`
 * binds first and the picture is 75vh * 2975/2505 ≈ 89vh wide. `index.html`
 * preloads with the same string, and index.css sets the widths — change all
 * three together.
 */
export const HERO_SIZES = '(min-width: 1368px) 1040px, (min-width: 1024px) 76vw, (orientation: landscape) 89vh, 100vw';

/** The master is 5120x2880; the crop the pipeline ships is 2975x2505. */
const W = 2975;
const H = 2505;

/**
 * The centerpiece, and the page's one object. Its only companion is the light
 * behind it (`.hero-light`), which the picture's #000 lets through.
 * Its baked-in labels are not crawlable, so `aria-describedby` points at the
 * `#how` section, which states every one of them as text.
 */
export function HeroPicture({ className }: { className?: string }) {
  // Not forced to high priority: a visitor who gets the 3D never sees this
  // picture, and it must not compete with the 3D download. For everyone else
  // index.html's head script preloads it at high priority (same srcset and
  // sizes as here — change both together). React 18 does not know
  // `fetchpriority`; passed through lowercase it reaches the DOM unchanged.
  const priority = { fetchpriority: 'auto' } as Record<string, string>;
  return (
    <picture className={className}>
      <source type="image/avif" srcSet={srcset('avif')} sizes={HERO_SIZES} />
      <source type="image/webp" srcSet={srcset('webp')} sizes={HERO_SIZES} />
      <img
        src={`${BASE}-1280.jpg`}
        width={W}
        height={H}
        alt={HERO_ALT}
        aria-describedby="how-lead"
        decoding="async"
        loading="eager"
        className="hero-img"
        {...priority}
      />
    </picture>
  );
}
